import { Card } from '@/components/ui/card';
import { CardTitle, Overline } from '@/components/ui/text';
import { OVERLINE } from '@/components/ui/recipes';
import { cn } from '@/utils/utils';
import { useSubmittedTemplateDetails } from '@/hooks/useSubmittedTemplateDetails';
import {
  TEMPLATE_DIFFICULTIES,
  isTemplateDifficulty,
} from '@/components/publish-flow/templateOptions';
import {
  CATEGORY_PILL,
  DIFFICULTY_ICON,
  DIFFICULTY_PILL,
  PILL_BASE,
  TAG_PILL,
} from '@/components/cms/ui/pill-styles';
import type { TemplateSubmissionStatus } from '@/types/templateSubmissionTypes';

interface Props {
  templateSceneOriginId: string | null;
  submissionStatus: TemplateSubmissionStatus | null;
}

// Read-only view of the template listing. The publish flow is the only place this
// is edited, so showing inputs here would put the same fields in two places again.
export function StoryTemplateSummaryCard({ templateSceneOriginId, submissionStatus }: Props) {
  const details = useSubmittedTemplateDetails(templateSceneOriginId);
  if (!templateSceneOriginId) return null;

  const hasMedia =
    !!details && (details.coverUrl || details.extraImageUrls.length > 0 || details.videoUrl);

  return (
    <Card>
      <CardTitle>Template details</CardTitle>
      <p className="mt-1 text-lureo-14 text-lureo-ink-subtle leading-5">{SUBLINE[submissionStatus ?? 'none']}</p>

      {!details ? (
        <p className="mt-4 text-lureo-13 text-lureo-ink-subtle">Loading template details...</p>
      ) : (
        <>
          {hasMedia && (
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {details.coverUrl && <MediaTile label="Cover" url={details.coverUrl} />}
              {details.extraImageUrls.map((url, i) => (
                <MediaTile key={i} label={`Extra ${i + 1}`} url={url} />
              ))}
              {details.videoUrl && <MediaTile label="Video" url={details.videoUrl} isVideo />}
            </div>
          )}

          <dl className="mt-5 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Category">
                {details.category ? (
                  <span className={`${PILL_BASE} px-3 capitalize ${CATEGORY_PILL}`}>
                    {details.category}
                  </span>
                ) : (
                  <NotSet />
                )}
              </Field>
              <Field label="Difficulty">
                {details.difficulty ? <DifficultyChip value={details.difficulty} /> : <NotSet />}
              </Field>
            </div>

            <Field label="Tags">
              {details.tags.length > 0 ? (
                <span className="flex flex-wrap gap-2">
                  {details.tags.map((tag) => (
                    <Chip key={tag}>{tag}</Chip>
                  ))}
                </span>
              ) : (
                <NotSet />
              )}
            </Field>

            <Field label="Description">
              {details.description ? (
                <span className="block whitespace-pre-wrap text-lureo-14 text-lureo-ink leading-5">
                  {details.description}
                </span>
              ) : (
                <NotSet />
              )}
            </Field>

            {!hasMedia && (
              <Field label="Media">
                <NotSet />
              </Field>
            )}
          </dl>

          <p className="mt-5 text-lureo-12 text-lureo-ink-subtle leading-4">
            {submissionStatus === 'accepted'
              ? 'Accepted templates stay as they were reviewed, so this listing is fixed.'
              : 'Use Update submission above to change any of this.'}
          </p>
        </>
      )}
    </Card>
  );
}

const SUBLINE: Record<TemplateSubmissionStatus | 'none', string> = {
  accepted: 'How your template appears to other creators in the gallery.',
  submitted: 'What our reviewers are looking at.',
  rejected: 'What you last submitted to the gallery.',
  draft: 'Your draft gallery listing.',
  none: 'What other creators will see in the gallery.',
};

function difficultyLabel(value: string): string {
  return TEMPLATE_DIFFICULTIES.find((d) => d.value === value)?.label ?? value;
}

function MediaTile({ label, url, isVideo }: { label: string; url: string; isVideo?: boolean }) {
  return (
    <div>
      <Overline as="span" className="mb-1.5 block">
        {label}
      </Overline>
      <div className="aspect-video w-full overflow-hidden rounded-lureo-radius-lg border-lureo-border-width border-lureo-line bg-lureo-neutral">
        {isVideo ? (
          <video src={url} controls preload="metadata" className="h-full w-full object-cover" />
        ) : (
          <img src={url} alt={label} className="h-full w-full object-cover" />
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className={cn(OVERLINE.field, 'mb-1.5')}>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return <span className={`${PILL_BASE} px-3 ${TAG_PILL}`}>{children}</span>;
}

function DifficultyChip({ value }: { value: string }) {
  if (!isTemplateDifficulty(value)) return <Chip>{value}</Chip>;
  const Icon = DIFFICULTY_ICON[value];
  return (
    <span className={`${PILL_BASE} px-3 ${DIFFICULTY_PILL[value]}`}>
      <Icon className="h-3.5 w-3.5" />
      {difficultyLabel(value)}
    </span>
  );
}

function NotSet() {
  return <span className="text-lureo-14 italic text-lureo-ink-subtlest leading-5">Not set</span>;
}
